import { useState, useEffect } from 'react';
import { 
  PersonneAContacter, 
  ClientPersonneAContacter, 
  TypeRelation,
  ApiResponse 
} from '../types/api';
import { apiService } from '../services/api';

export const useContactPersons = () => {
  const [contactPersons, setContactPersons] = useState<PersonneAContacter[]>([]);
  const [clientRelations, setClientRelations] = useState<ClientPersonneAContacter[]>([]);
  const [relations, setRelations] = useState<TypeRelation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContactPersons = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getPersonnesAContacter();
      setContactPersons(response.results);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement des personnes à contacter');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientRelations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getClientPersonnesAContacter();
      setClientRelations(response.results);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement des relations');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getTypesRelation();
      setRelations(response.results);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement des types de relations');
    } finally {
      setLoading(false);
    }
  };

  const createContactPerson = async (data: Partial<PersonneAContacter>) => {
    try {
      setLoading(true);
      setError(null);
      const newPerson = await apiService.createPersonneAContacter(data);
      setContactPersons(prev => [...prev, newPerson]);
      return newPerson;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors de la création de la personne à contacter');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateContactPerson = async (id: number, data: Partial<PersonneAContacter>) => {
    try {
      setLoading(true);
      setError(null);
      const updatedPerson = await apiService.updatePersonneAContacter(id, data);
      setContactPersons(prev => 
        prev.map(person => person.id === id ? updatedPerson : person)
      );
      return updatedPerson;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise à jour de la personne à contacter');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteContactPerson = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      await apiService.deletePersonneAContacter(id);
      setContactPersons(prev => prev.filter(person => person.id !== id));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression de la personne à contacter');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createClientRelation = async (data: Partial<ClientPersonneAContacter>) => {
    try {
      setLoading(true);
      setError(null);
      const newRelation = await apiService.createClientPersonneAContacter(data);
      setClientRelations(prev => [...prev, newRelation]);
      return newRelation;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors de la création de la relation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateClientRelation = async (id: number, data: Partial<ClientPersonneAContacter>) => {
    try {
      setLoading(true);
      setError(null);
      const updatedRelation = await apiService.updateClientPersonneAContacter(id, data);
      setClientRelations(prev => 
        prev.map(relation => relation.id === id ? updatedRelation : relation)
      );
      return updatedRelation;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise à jour de la relation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteClientRelation = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      await apiService.deleteClientPersonneAContacter(id);
      setClientRelations(prev => prev.filter(relation => relation.id !== id));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression de la relation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactPersons();
    fetchClientRelations();
    fetchRelations();
  }, []);

  return {
    contactPersons,
    clientRelations,
    relations,
    loading,
    error,
    fetchContactPersons,
    fetchClientRelations,
    fetchRelations,
    createContactPerson,
    updateContactPerson,
    deleteContactPerson,
    createClientRelation,
    updateClientRelation,
    deleteClientRelation,
  };
};
